import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { adminAPI, APIError } from "../lib/api";
import type { AdminOrganization } from "../types/api";
import { TierBadge, StatusBadge } from "../components/Badge";
import { useKeyboard } from "../hooks/useKeyboard";
import styles from "./OrganizationList.module.css";

type SortField = "name" | "created" | "members" | "status";
type SortOrder = "asc" | "desc";

export function OrganizationList() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortField>("created");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const cursorHistory = useRef<string[]>([]);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminAPI.listOrganizations({
        search: search || undefined,
        sort,
        order,
        cursor,
        limit: 25,
      });

      setOrganizations(data.organizations);
      setNextCursor(data.next_cursor);
      setTotalCount(data.total_count);
      setSelectedIndex(0);
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      } else {
        setError("Failed to load organizations");
      }
    } finally {
      setLoading(false);
    }
  }, [search, sort, order, cursor]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCursor(undefined);
      cursorHistory.current = [];
      fetchOrganizations();
    }, 300);

    return () => clearTimeout(debounce);
  }, [search, sort, order, fetchOrganizations]);

  useEffect(() => {
    if (cursor !== undefined) {
      fetchOrganizations();
    }
  }, [cursor, fetchOrganizations]);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [newSort, newOrder] = e.target.value.split("-") as [SortField, SortOrder];
    setSort(newSort);
    setOrder(newOrder);
  };

  const handleNextPage = () => {
    if (nextCursor && cursor) {
      cursorHistory.current.push(cursor);
    }
    setCursor(nextCursor || undefined);
  };

  const handlePrevPage = () => {
    const prevCursor = cursorHistory.current.pop();
    setCursor(prevCursor || undefined);
  };

  const handleRowClick = (org: AdminOrganization) => {
    navigate(`/organizations/${org.id}`);
  };

  useKeyboard({
    onNavigateDown: () => {
      setSelectedIndex((prev) => Math.min(prev + 1, organizations.length - 1));
    },
    onNavigateUp: () => {
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    },
    onNavigateTop: () => {
      setSelectedIndex(0);
    },
    onNavigateBottom: () => {
      setSelectedIndex(organizations.length - 1);
    },
    onSelect: () => {
      if (organizations[selectedIndex]) {
        handleRowClick(organizations[selectedIndex]);
      }
    },
    onSearch: () => {
      searchInputRef.current?.focus();
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Organizations</h1>

        <div className={styles.controls}>
          <div className={styles.searchWrapper}>
            <svg
              className={styles.searchIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search by name, slug, or owner..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-search-input
            />
          </div>

          <select
            className={styles.sortSelect}
            value={`${sort}-${order}`}
            onChange={handleSortChange}
          >
            <option value="created-desc">Newest first</option>
            <option value="created-asc">Oldest first</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="members-desc">Most members</option>
            <option value="members-asc">Fewest members</option>
          </select>
        </div>
      </header>

      {loading && <div className={styles.loading}>Loading organizations...</div>}

      {error && <div className={styles.error}>{error}</div>}

      {!loading && !error && organizations.length === 0 && (
        <div className={styles.empty}>
          {search ? "No organizations found matching your search." : "No organizations yet."}
        </div>
      )}

      {!loading && !error && organizations.length > 0 && (
        <>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Organization</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Members</th>
                <th>Owner</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org, index) => (
                <tr
                  key={org.id}
                  className={`${styles.tableRow} ${index === selectedIndex ? styles.tableRowSelected : ""}`}
                  onClick={() => handleRowClick(org)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleRowClick(org);
                  }}
                  tabIndex={0}
                >
                  <td>
                    <div className={styles.orgInfo}>
                      <span className={styles.orgName}>{org.name}</span>
                      <span className={styles.orgSlug}>{org.slug}</span>
                    </div>
                  </td>
                  <td>
                    <TierBadge tier={org.subscription_tier} />
                  </td>
                  <td>
                    <StatusBadge status={org.subscription_status} />
                  </td>
                  <td>
                    <span className={styles.memberCount}>{org.member_count}</span>
                  </td>
                  <td>
                    <span className={styles.email}>{org.owner_email || "-"}</span>
                  </td>
                  <td>
                    <span className={styles.date}>{formatDate(org.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              Showing {organizations.length} of {totalCount} organizations
            </span>
            <div className={styles.paginationButtons}>
              <button
                className={styles.paginationButton}
                onClick={handlePrevPage}
                disabled={cursorHistory.current.length === 0 && !cursor}
              >
                Previous
              </button>
              <button
                className={styles.paginationButton}
                onClick={handleNextPage}
                disabled={!nextCursor}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
